import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import DashboardLayout from '@/components/layout/DashboardLayout';
import { StripeWidget } from '@/components/organizer/stripe-widget';
import SecuritySettingsPanel from '@/components/security/SecuritySettingsPanel';
import GdprSettingsPanel from '@/components/settings/GdprSettingsPanel';
import NotificationPreferencesPanel from '@/components/settings/NotificationPreferencesPanel';
import type { NotificationCategory } from '@/components/settings/NotificationPreferencesPanel';
import SubscriptionPanel from '@/components/settings/SubscriptionPanel';
import type { SubscriptionPlanConfig } from '@/components/settings/SubscriptionPanel';
import PageHeader from '@/components/ui/PageHeader';
import { ROUTES } from '@/config/routes';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import {
  getOrganizationApi,
  stripeBillingPortalApi,
  stripeSetupIntentApi,
  stripeSubscribeApi,
  updateOrganizationApi,
} from '@/lib/api/organizationsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { useSaasStore } from '@/stores/saas-store';
import { FORFAIT_ENABLED, ORGA_FORFAIT_ANNUAL_TTC_CENTS, ORGA_FORFAIT_MONTHLY_TTC_CENTS } from '@shared/constants';
import { isOrgaForfaitActive } from '@shared/utils/premium.utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Check,
  Copy,
  CreditCard,
  Database,
  Eye,
  EyeOff,
  Gift,
  Link2,
  Mail,
  Settings,
  Share2,
  Shield,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Tab definitions ───────────────────────────────────────────

type TabId = 'security' | 'subscription' | 'stripe' | 'referral' | 'notifications' | 'gdpr' | 'preferences';

interface TabDef {
  id: TabId;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  soon?: true;
}

const TABS: TabDef[] = [
  {
    id: 'security',
    label: 'Securite',
    description: '2FA, appareils de confiance',
    icon: Shield,
  },
  {
    id: 'subscription',
    label: 'Abonnement',
    description: 'Plan organisateur',
    icon: CreditCard,
  },
  {
    id: 'stripe',
    label: 'Paiement Stripe',
    description: 'Encaissements et activation',
    icon: CreditCard,
  },
  {
    id: 'referral',
    label: 'Parrainage',
    description: 'Code parrainage et recompenses',
    icon: Gift,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    description: 'Alertes email et push',
    icon: Bell,
  },
  {
    id: 'gdpr',
    label: 'Donnees personnelles',
    description: 'Export RGPD, suppression de compte',
    icon: Database,
  },
  {
    id: 'preferences',
    label: 'Preferences',
    description: 'Visibilite du profil et des concours',
    icon: Settings,
  },
];

const DEFAULT_TAB: TabId = 'security';

const ORGANIZER_NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    id: 'registration',
    label: 'Inscriptions a mes concours',
    description: "Nouvelles inscriptions, annulations, listes d'attente",
    email: true,
  },
  {
    id: 'payments',
    label: 'Paiements recus',
    description: 'Confirmations de paiement, remboursements',
    email: true,
  },
  {
    id: 'reminders',
    label: 'Rappels concours',
    description: 'Rappels avant le debut de vos concours',
    email: true,
  },
  {
    id: 'results',
    label: 'Resultats valides',
    description: 'Validation des scores et classements',
    email: false,
  },
  {
    id: 'platform',
    label: 'Actualites plateforme',
    description: 'Nouveautes, maintenances, mises a jour',
    email: false,
  },
];

// ─── Settings Nav ──────────────────────────────────────────────

interface SettingsNavProps {
  activeTab: TabId;
  onSelect: (tab: TabId) => void;
  tabs: TabDef[];
}

/**
 * Responsive behavior:
 * - Mobile: horizontal scroll “pill” nav (label only for active tab)
 * - Desktop: left vertical nav
 */
function SettingsNav({ activeTab, onSelect, tabs }: SettingsNavProps) {
  return (
    <>
      {/* Mobile nav */}
      <nav className='md:hidden -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:px-0 overflow-x-auto'>
        <div className='flex gap-2 pb-2'>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                type='button'
                onClick={() => !tab.soon && onSelect(tab.id)}
                disabled={tab.soon}
                className={`shrink-0 inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                } ${tab.soon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                <span className={`whitespace-nowrap ${isActive ? 'inline-block' : 'hidden md:inline-block'}`}>
                  {tab.label}
                </span>
                {tab.soon && (
                  <span className='text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded'>
                    bientot
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Desktop nav */}
      <nav className='hidden md:flex flex-col gap-1 w-64 shrink-0'>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type='button'
              onClick={() => !tab.soon && onSelect(tab.id)}
              disabled={tab.soon}
              className={`flex items-start gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors group
								${isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}
								${tab.soon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
							`}
            >
              <Icon
                className={`w-4 h-4 mt-0.5 shrink-0 ${
                  isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'
                }`}
              />
              <div className='min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className={`text-sm font-semibold ${isActive ? 'text-blue-700' : ''}`}>{tab.label}</span>
                  {tab.soon && (
                    <span className='text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded'>
                      bientot
                    </span>
                  )}
                </div>
                <p className='text-xs text-slate-400 mt-0.5 truncate'>{tab.description}</p>
              </div>
            </button>
          );
        })}
      </nav>
    </>
  );
}

// ─── Referral Panel ─────────────────────────────────────────────

function ReferralPanel() {
  const user = useAuthStore(s => s.user);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');

  const frontUrl =
    (import.meta.env.VITE_URL_FRONT as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5232';
  const referralLink = user?.referralCode ? `${frontUrl}/inscription?ref=${user.referralCode}` : '';

  function copyReferralCode() {
    if (!user?.referralCode) return;
    navigator.clipboard.writeText(user.referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function copyReferralLink() {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  function sendByEmail() {
    if (!shareEmail || !referralLink) return;
    const subject = encodeURIComponent('Rejoins BoulesConnect avec mon code parrainage !');
    const body = encodeURIComponent(
      `Salut !\n\nJe t'invite a rejoindre BoulesConnect pour gerer tes concours de petanque.\n\nInscris-toi avec mon lien et on gagne chacun 30€ de credit commission :\n${referralLink}\n\nA bientot sur BoulesConnect !`,
    );
    window.open(`mailto:${shareEmail}?subject=${subject}&body=${body}`, '_blank');
    setShareEmail('');
    setShowShareModal(false);
  }

  return (
    <>
      <div className='space-y-6 max-w-2xl'>
        <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
          <div className='flex items-center gap-3 mb-6'>
            <div className='w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center'>
              <Gift className='w-5 h-5 text-indigo-600' />
            </div>
            <div className='min-w-0'>
              <h3 className='font-bold text-slate-900'>Mon code parrainage</h3>
              <p className='text-sm text-slate-500'>
                Parrainez des organisateurs et gagnez 30€ de credit commission chacun
              </p>
            </div>
          </div>

          <p className='text-sm text-slate-600 mb-5 leading-relaxed max-w-2xl'>
            Partagez votre code ou lien d&apos;inscription avec d&apos;autres organisateurs. Quand un filleul effectue
            son premier paiement de commission, vous recevez chacun 30€ de credit (max 5 filleuls, plafond 150€).
          </p>

          {/* Code + copy */}
          <div className='flex flex-col sm:flex-row sm:items-center gap-3 max-w-sm mb-4'>
            <input
              readOnly
              value={user?.referralCode ?? '—'}
              className='w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 font-mono text-xl font-extrabold tracking-widest text-slate-900 select-all cursor-text'
            />
            <button
              onClick={copyReferralCode}
              disabled={!user?.referralCode}
              title='Copier le code'
              aria-label='Copier le code de parrainage'
              className='w-full sm:w-12 cursor-pointer flex h-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-40'
            >
              {copied ? <Check className='w-5 h-5 text-emerald-500' /> : <Copy className='w-5 h-5' />}
            </button>
          </div>
          {copied && <p className='mb-4 text-sm font-semibold text-emerald-600'>Code copie dans le presse-papiers !</p>}

          {/* Share button */}
          <button
            onClick={() => setShowShareModal(true)}
            disabled={!user?.referralCode}
            className='cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm transition hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed'
          >
            <Share2 className='w-4 h-4' />
            Partager mon lien de parrainage
          </button>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
          <div className='bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl'>
            <div className='flex items-center justify-between mb-5'>
              <h3 className='text-lg font-bold text-slate-900'>Partager mon lien de parrainage</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className='cursor-pointer p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition'
                aria-label='Fermer'
              >
                <X className='w-5 h-5' />
              </button>
            </div>

            {/* Referral link + copy */}
            <div className='mb-5'>
              <label className='block text-sm font-semibold text-slate-700 mb-2'>Lien d&apos;inscription</label>
              <div className='flex items-center gap-2'>
                <div className='relative flex-1 min-w-0'>
                  <Link2 className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                  <input
                    readOnly
                    value={referralLink}
                    className='w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-700 select-all cursor-text truncate'
                  />
                </div>
                <button
                  onClick={copyReferralLink}
                  title='Copier le lien'
                  className='cursor-pointer flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600'
                >
                  {linkCopied ? <Check className='w-4 h-4 text-emerald-500' /> : <Copy className='w-4 h-4' />}
                </button>
              </div>
              {linkCopied && (
                <p className='mt-1.5 text-xs font-semibold text-emerald-600'>Lien copie dans le presse-papiers !</p>
              )}
            </div>

            {/* Send by email */}
            <div>
              <label htmlFor='shareEmail' className='block text-sm font-semibold text-slate-700 mb-2'>
                Envoyer par email
              </label>
              <div className='flex flex-col sm:flex-row sm:items-center gap-2'>
                <div className='relative flex-1 min-w-0'>
                  <Mail className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                  <input
                    id='shareEmail'
                    type='email'
                    value={shareEmail}
                    onChange={e => setShareEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sendByEmail()}
                    placeholder='email@exemple.com'
                    className='w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                  />
                </div>
                <button
                  onClick={sendByEmail}
                  disabled={!shareEmail}
                  className='cursor-pointer inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm transition hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed'
                >
                  <Mail className='w-4 h-4' />
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Organizer Subscription Panel ─────────────────────────────────

function OrganizerSubscriptionPanel() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const authOrg = user?.organizationsAdmin?.[0]?.organization;
  const orgId = authOrg?.id;

  const { data: fullOrg } = useQuery({
    queryKey: queryKeys.organizations.detail(orgId!),
    queryFn: () => getOrganizationApi(orgId!),
    enabled: !!orgId && isAuthenticated,
  });

  const creditCents = fullOrg?.commissionCreditRemainingCents ?? 0;

  if (!FORFAIT_ENABLED) {
    return (
      <div className='max-w-2xl space-y-4'>
        <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
          <div className='flex items-center gap-3 mb-4'>
            <div className='w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center'>
              <CreditCard className='w-5 h-5 text-slate-400' />
            </div>
            <div className='min-w-0'>
              <h3 className='font-bold text-slate-900'>Commission Partenaire</h3>
              <p className='text-sm text-slate-500'>Votre plan actuel</p>
            </div>
          </div>

          <p className='text-sm text-slate-600 leading-relaxed'>
            Vous beneficiez du plan Commission Partenaire : 2 % + 0,30 € par inscription, avec un minimum de 0,75 €. La
            commission est prelevee uniquement lors de paiements reels pour des concours payants.
          </p>
        </div>

        {creditCents > 0 && (
          <div className='rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3'>
            <p className='text-sm font-semibold text-emerald-800'>
              Crédit commission disponible : {(creditCents / 100).toFixed(2).replace('.', ',')} €
            </p>
            <p className='mt-0.5 text-xs text-emerald-700'>Appliqué automatiquement sur vos prochaines commissions</p>
          </div>
        )}
      </div>
    );
  }

  if (!authOrg) {
    return (
      <div className='max-w-2xl'>
        <div className='bg-white rounded-2xl p-4 sm:p-6 border border-dashed border-slate-300'>
          <p className='text-sm text-slate-500'>Vous devez etre associe a une organisation pour gerer un abonnement.</p>
        </div>
      </div>
    );
  }

  const isSubscribed = isOrgaForfaitActive(authOrg.plan, authOrg.subscriptionStatus);

  const planConfig: SubscriptionPlanConfig = {
    planName: 'Forfait Organisateur',
    description: 'Passez au forfait pour organiser vos concours sans commission sur les inscriptions.',
    priceMonthly: `${ORGA_FORFAIT_MONTHLY_TTC_CENTS / 100}`,
    priceAnnual: `${ORGA_FORFAIT_ANNUAL_TTC_CENTS / 100}`,
    features: [
      '0 % de commission sur les inscriptions (au lieu de 2 % + 0,30 €, min 0,75 €)',
      'Nombre illimite de concours',
      'Statistiques avancees et exports',
      'Support prioritaire',
    ],
    isSubscribed,
    onSetupIntent: () => stripeSetupIntentApi(authOrg.id),
    onSubscribe: (interval, paymentMethodId) => stripeSubscribeApi(authOrg.id, interval, paymentMethodId),
    onBillingPortal: isSubscribed ? () => stripeBillingPortalApi(authOrg.id) : null,
    accentColor: 'blue',
    invalidateKeys: [queryKeys.organizations.detail(authOrg.id)],
  };

  return (
    <div className='space-y-4'>
      <SubscriptionPanel config={planConfig} />

      {creditCents > 0 && (
        <div className='max-w-2xl rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3'>
          <p className='text-sm font-semibold text-emerald-800'>
            Crédit commission disponible : {(creditCents / 100).toFixed(2).replace('.', ',')} €
          </p>
          <p className='mt-0.5 text-xs text-emerald-700'>Appliqué automatiquement sur vos prochaines commissions</p>
        </div>
      )}
    </div>
  );
}

// ─── Organizer Stripe Payment Panel ─────────────────────────────

function OrganizerStripePaymentPanel() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const orgId = user?.organizationsAdmin?.[0]?.organizationId;

  const { data: fullOrg, isLoading } = useQuery({
    queryKey: queryKeys.organizations.detail(orgId ?? 0),
    queryFn: () => getOrganizationApi(orgId!),
    enabled: !!orgId && isAuthenticated,
  });

  if (!orgId) {
    return (
      <div className='max-w-2xl'>
        <div className='bg-white rounded-2xl p-4 sm:p-6 border border-dashed border-slate-300'>
          <p className='text-sm text-slate-500'>
            Vous devez etre associe a une organisation pour configurer Stripe Connect.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className='max-w-2xl h-56 animate-pulse rounded-2xl bg-slate-100' />;
  }

  if (!fullOrg) {
    return (
      <div className='max-w-2xl'>
        <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
          <p className='text-sm text-slate-500'>Impossible de charger les informations de paiement pour le moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-2xl space-y-4'>
      <div className='rounded-2xl border border-slate-200 bg-white p-6'>
        <p className='font-semibold text-slate-900'>SIRET : {fullOrg.siret ?? 'Non renseigné'}</p>
        {fullOrg.siren && <p className='mt-1 text-sm text-slate-600'>SIREN : {fullOrg.siren}</p>}
        <Link
          to={`${ROUTES.ORGANIZER_PROFILE_EDIT}#organization-siret`}
          className='mt-3 inline-block text-sm font-semibold text-blue-600 underline'
        >
          {fullOrg.siret ? 'Modifier le SIRET de mon organisation' : 'Renseigner le SIRET de mon organisation'}
        </Link>
      </div>

      <StripeWidget
        orgId={fullOrg.id}
        stripeAccountStatus={fullOrg.stripeAccountStatus}
        stripeTestMode={fullOrg.stripeTestMode}
        sirenMissing={!fullOrg.siren}
      />

      <div className='rounded-2xl border border-blue-100 bg-blue-50 p-4 sm:p-5'>
        <p className='text-sm font-semibold text-blue-900'>Impact sur vos concours payants</p>
        <p className='mt-1 text-sm text-blue-800'>
          Les concours gratuits restent disponibles. Les concours payants sont bloques tant que Stripe Connect
          n&apos;est pas actif, afin d&apos;eviter une inscription impossible a payer cote joueur.
        </p>
      </div>
    </div>
  );
}

// ─── Preferences Panel ────────────────────────────────────────

function PreferencesPanel() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const orgId = user?.organizationsAdmin?.[0]?.organizationId ?? user?.organizationsAdmin?.[0]?.organization?.id;
  const queryClient = useQueryClient();

  const { data: organization } = useQuery({
    queryKey: queryKeys.organizations.detail(orgId!),
    queryFn: () => getOrganizationApi(orgId!),
    enabled: !!orgId && isAuthenticated,
  });

  const isProfilePublic = organization?.isProfilePublic ?? true;

  const toggleMutation = useMutation({
    mutationFn: (value: boolean) => updateOrganizationApi(orgId!, { isProfilePublic: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.detail(orgId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
      toast.success('Preferences mises à jour');
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Erreur lors de la mise à jour');
    },
  });

  if (!orgId) {
    return (
      <div className='max-w-2xl'>
        <div className='bg-white rounded-2xl p-4 sm:p-6 border border-dashed border-slate-300'>
          <p className='text-sm text-slate-500'>
            Vous devez etre associe a une organisation pour gerer les preferences.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6 max-w-2xl'>
      <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center'>
            {isProfilePublic ? (
              <Eye className='w-5 h-5 text-blue-600' />
            ) : (
              <EyeOff className='w-5 h-5 text-slate-400' />
            )}
          </div>
          <div className='min-w-0'>
            <h3 className='font-bold text-slate-900'>Visibilite publique</h3>
            <p className='text-sm text-slate-500 truncate'>
              Controlez la visibilite de votre organisation sur le site public
            </p>
          </div>
        </div>

        <div className='space-y-4'>
          <div className='flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-xl'>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2'>
                <span className='font-semibold text-sm text-slate-900'>Profil organisateur public</span>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    isProfilePublic ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {isProfilePublic ? 'visible' : 'masque'}
                </span>
              </div>
              <p className='text-xs text-slate-500 mt-1'>
                {isProfilePublic
                  ? 'Votre organisation apparait dans la recherche. Son profil et ses concours publics sont accessibles.'
                  : 'Votre organisation est masquee de la recherche. Son profil et ses concours ne sont plus visibles publiquement.'}
              </p>
            </div>

            <button
              type='button'
              onClick={() => toggleMutation.mutate(!isProfilePublic)}
              disabled={toggleMutation.isPending}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
                isProfilePublic ? 'bg-blue-500' : 'bg-slate-300'
              }`}
              aria-label='Basculer la visibilite publique'
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isProfilePublic ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Panel content ─────────────────────────────────────────────

function TabContent({ tab }: { tab: TabId }) {
  switch (tab) {
    case 'security':
      return <SecuritySettingsPanel />;
    case 'subscription':
      return <OrganizerSubscriptionPanel />;
    case 'stripe':
      return <OrganizerStripePaymentPanel />;
    case 'referral':
      return <ReferralPanel />;
    case 'notifications':
      return <NotificationPreferencesPanel categories={ORGANIZER_NOTIFICATION_CATEGORIES} accentColor='blue' />;
    case 'gdpr':
      return <GdprSettingsPanel accentColor='blue' />;
    case 'preferences':
      return <PreferencesPanel />;
    default:
      return null;
  }
}

// ─── Main ──────────────────────────────────────────────────────

export default function OrganizerSettings() {
  const referralEnabled = useSaasStore(s => s.referralEnabled);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as TabId | null;

  const tabs = referralEnabled ? TABS : TABS.filter(t => t.id !== 'referral');

  const activeTab: TabId = rawTab && tabs.some(t => t.id === rawTab && !t.soon) ? rawTab : DEFAULT_TAB;

  function handleSelect(tab: TabId) {
    setSearchParams({ tab }, { replace: true });
  }

  const currentTab = tabs.find(t => t.id === activeTab)!;

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
        <PageHeader title='Parametres' subtitle='Configuration et securite de votre espace organisateur' />

        {/* Layout responsive:
					- Mobile: horizontal nav + stacked content
					- Desktop: left nav + divider + content
				*/}
        <div className='mt-6 flex flex-col md:flex-row md:gap-8'>
          {/* Left nav */}
          <div className='md:shrink-0'>
            <SettingsNav activeTab={activeTab} onSelect={handleSelect} tabs={tabs} />
          </div>

          {/* Divider (desktop only) */}
          <div className='hidden md:block w-px bg-slate-200 self-stretch' />

          {/* Content */}
          <div className='flex-1 min-w-0 mt-4 md:mt-0'>
            <div className='mb-6'>
              <h2 className='text-xl font-bold text-slate-900'>{currentTab.label}</h2>
              <p className='text-sm text-slate-500 mt-0.5'>{currentTab.description}</p>
            </div>

            <TabContent tab={activeTab} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
